interface OpenReportWindowOptions {
  title: string;
  styleText: string;
  windowName?: string;
  targetName?: string;   // 팝업이 클릭 시점에 미리 열어 둔 탭 이름 — 있으면 새 탭 대신 그 탭에 붙는다
}

/**
 * 메시지 segments 안에서 "미리 연 탭 이름"이 들어갈 자리 표시.
 * buildBroadcastHyperlink(…, newTabPrefix) 는 이 값이 있으면 그 자리를, 없으면 맨 끝에 탭 이름을 넣는다.
 * 예) ["BusinessChartDashboard", baseId, baseName, date, department, baseType, NEW_TAB_SLOT, "1"]
 *     → 리스너의 args[6] = 탭 이름, args[7] = allowFallback 이 그대로 유지된다.
 */
export const NEW_TAB_SLOT = "@NEWTAB@";

/** 리포트 창마다 들어가는 팝업 차단 안내 배너(#popup-blocked). app-root 밖이라 renderReportIntoWindow 로 지워지지 않는다 */
const POPUP_BLOCKED_BANNER = `
  <div id="popup-blocked" style="display:none;position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:100000;
       max-width:560px;background:#FFF9EC;border:1px solid #E8D6AC;border-left:4px solid #B8862E;border-radius:6px;
       padding:10px 14px;font:12px/1.6 -apple-system,'Segoe UI',Pretendard,Arial,sans-serif;color:#101B26;
       box-shadow:0 8px 24px rgba(10,31,51,.25);">
    <b>Pop-up blocked.</b> Allow pop-ups for this site (pop-up icon at the right end of the address bar &rarr; <b>Always allow</b>),
    then click the link again.
    <a href="javascript:void(0)" onclick='document.getElementById("popup-blocked").style.display="none"'
       style="margin-left:8px;color:#1668DC;">Close</a>
  </div>
  <style>@media print { #popup-blocked { display:none !important; } }</style>`;

/** 미리 연 탭에 메인 창이 그리기 전까지 보여 줄 문구 — 메인 창이 닫혀 있어 응답이 없을 때도 빈 탭으로 남지 않게 */
const PREOPEN_WAITING_HTML =
  `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Please Wait...</title></head>` +
  `<body style="font:13px -apple-system,'Segoe UI',Arial,sans-serif;color:#8A98A2;padding:40px;">` +
  `Please Wait...<br><br><small>If this page stays blank, keep the main iFMS window open and click the link again.</small>` +
  `</body></html>`;

export function openLoadingWindow(options: OpenReportWindowOptions): Window | null {
  // 드릴다운 링크가 window.open("", windowName) 으로 메인 창을 찾으므로, 팝업이 막혀도 이름은 먼저 붙여 둔다
  if (options.windowName) window.name = options.windowName;

  const win = window.open("", options.targetName || "_blank");
  if (!win) {
    // targetName 이 있으면 클릭한 리포트 창에서 미리 연 탭을 못 찾은 경우(사용자가 닫음 등).
    // 이때 안내는 클릭한 창이 이미 했고, 메인 창은 뒤에 있어 alert 를 띄워도 보이지 않으므로 조용히 끝낸다.
    if (!options.targetName) alert("Please allow popups for this website.");
    return null;
  }

  win.document.open();
  win.document.write(`<!DOCTYPE html>
<html lang="ko">
<head><meta charset="UTF-8" /><title>${options.title}</title>
<style>${options.styleText}</style></head>
<body>
  ${POPUP_BLOCKED_BANNER}
  <div id="app-root">
    <div class="state-wrap">
      <div class="spinner"></div>
      <div class="state-text">${"Please Wait..."}</div>
    </div>
  </div>
</body>
</html>`);
  win.document.close();
  return win;
}

export function renderReportIntoWindow(win: Window, innerHtml: string): void {
  if (win.closed) return;
  const root = win.document.getElementById("app-root");
  if (root) root.innerHTML = innerHtml;
}

export function renderErrorIntoWindow(win: Window, message: string): void {
  if (win.closed) return;
  const root = win.document.getElementById("app-root");
  if (root) root.innerHTML = `<div class="state-wrap"><div class="state-text error">${message}</div></div>`;
}

/**
 * 리포트 창 안의 링크 — 클릭하면 BroadcastChannel 로 메인 창에 segments 를 보낸다.
 * @param windowName   있으면 보낸 뒤 그 이름의 창(메인 창)을 앞으로 가져온다(드릴다운용).
 * @param newTabPrefix 있으면 메인 창이 새 창을 여는 메시지다. 클릭 이벤트 안에서 탭을 미리 열고(사용자 클릭이라 차단되지 않음)
 *                     그 이름을 segments 의 NEW_TAB_SLOT 자리(없으면 맨 끝)에 넣어 보낸다. 메인 창은 그 탭에 붙어서 그린다.
 *                     미리 열기조차 막히면 메시지를 보내지 않고 이 창의 #popup-blocked 배너(없으면 alert)로 안내한다.
 */
export function buildBroadcastHyperlink(
  label: string,
  channelName: string,
  segments: (string | number)[],
  windowName?: string,
  newTabPrefix?: string
): string {
  const parts = segments.map(s => String(s));
  const hasSlot = parts.includes(NEW_TAB_SLOT);
  const encoded = encodeURIComponent(parts.join("^"));

  const js = (value: string): string => JSON.stringify(value).replace(/'/g, "&#39;");

  const focusCode = windowName
    ? `window.open(${js("")}, ${js(windowName)})?.focus();`
    : "";

  const openCode = newTabPrefix
    ? `var tab = ${js(newTabPrefix)} + "-" + Date.now();
       var w = window.open(${js("")}, tab);
       if (!w) {
         var b = document.getElementById("popup-blocked");
         if (b) b.style.display = "block";
         else alert(${js("Pop-up blocked. Please allow pop-ups for this website and click the link again.")});
         return;
       }
       try { w.document.open(); w.document.write(${js(PREOPEN_WAITING_HTML)}); w.document.close(); } catch (e) {}`
    : "";

  const targetCode = !newTabPrefix
    ? js(encoded)
    : hasSlot
      ? `${js(encoded)}.replace(${js(encodeURIComponent(NEW_TAB_SLOT))}, encodeURIComponent(tab))`
      : `${js(encoded + "%5E")} + encodeURIComponent(tab)`;

  return `<a href="javascript:void(0)" class="drill-link"
    onclick='(function(){
      ${openCode}
      var bc = new BroadcastChannel(${js(channelName)});
      bc.postMessage({ Target: ${targetCode} });
      bc.close();
      ${focusCode}
    })()'>${label}</a>`;
}

export function fmtAsOf(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${d.getFullYear()}`;
}